import { eachDayOfInterval, format } from 'date-fns';
import {
  TokenUsageAgentEntry,
  TokenUsageDailyEntry,
  TokenUsageModelEntry,
  TokenUsageProviderEntry,
  TokenUsageResponse,
  TokenUsageUserEntry,
} from 'features/usage/services/usage-service';
import { getRatio, parseDateOnly } from './usage-format';

export type UsageDay = {
  date: string;
  inactive: boolean;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  turn_count: number;
};

export type UsageViewModel = {
  allDays: UsageDay[];
  periodDays: string[];
  peakTokens: number;
  promptShare: number;
  completionShare: number;
  completionRatio: string;
  orderedModels: TokenUsageModelEntry[];
  byProvider: TokenUsageProviderEntry[];
  byAgent: TokenUsageAgentEntry[];
  byUser: TokenUsageUserEntry[];
};

export function buildUsageViewModel(data: TokenUsageResponse): UsageViewModel {
  const daily = data.breakdowns?.daily ?? [];
  const byModel = data.breakdowns?.by_model ?? [];
  const byProvider = data.breakdowns?.by_provider ?? [];
  const byUser = [...(data.breakdowns?.by_user ?? [])]
    .sort((left, right) => right.total_tokens - left.total_tokens)
    .slice(0, 10);
  const byAgent = [...(data.breakdowns?.by_agent ?? [])]
    .sort((left, right) => right.total_tokens - left.total_tokens)
    .slice(0, 10);

  const periodDays = eachDayOfInterval({
    start: parseDateOnly(data.period.start),
    end: parseDateOnly(data.period.end),
  }).map((day) => format(day, 'yyyy-MM-dd'));

  const dailyMap = new Map<string, TokenUsageDailyEntry>(daily.map((item) => [item.date, item] as const));

  const allDays = periodDays.map((date) => {
    const dailyEntry = dailyMap.get(date);

    return {
      date,
      inactive: !dailyEntry,
      input_tokens: dailyEntry?.input_tokens ?? 0,
      output_tokens: dailyEntry?.output_tokens ?? 0,
      total_tokens: dailyEntry?.total_tokens ?? 0,
      turn_count: dailyEntry?.turn_count ?? 0,
    };
  });

  const peakTokens = daily.length > 0 ? Math.max(...daily.map((day) => day.total_tokens)) : 0;
  const promptShare = data.totals.total_tokens
    ? (data.totals.input_tokens / data.totals.total_tokens) * 100
    : 0;
  const completionShare = data.totals.total_tokens
    ? (data.totals.output_tokens / data.totals.total_tokens) * 100
    : 0;
  const knownModels = byModel.filter((model) => model.model !== 'unknown');
  const unknownModels = byModel.filter((model) => model.model === 'unknown');

  return {
    allDays,
    periodDays,
    peakTokens,
    promptShare,
    completionShare,
    completionRatio: getRatio(data.totals.input_tokens, data.totals.output_tokens),
    orderedModels: [...knownModels, ...unknownModels],
    byProvider,
    byAgent,
    byUser,
  };
}
